import React, { useEffect, useRef } from "react";

/**
 * SectionStrip — issue #1128 (D216 section strip, D221 O2). The in-page
 * switch between a screen's few sections ("My training", "Guides"): a light
 * rounded track, items at least 44px tall (`--nw-size-tap`), the current item
 * a white pill with a hairline shadow. It is NOT a second menu: the ribbon
 * stays the one menu (G2), and this strip sits under it, above the heading
 * (D225 item 2, Q2).
 *
 * `items = [{ key, label, testId }]`, `currentKey`, `onChange(key)`. The
 * container carries `role="tablist"` and the testid the caller passes, which
 * must end in `-sections` so journey 60 reads it as a strip; each item is a
 * `button role="tab"` with `aria-selected`. The strip scrolls sideways with
 * its scrollbar hidden when its items outgrow the width, and the current item
 * is scrolled into view on mount and whenever `currentKey` changes (the way
 * `Ribbon` does it, `nearest` on both axes so the page itself never moves).
 *
 * KEYBOARD AND SCREEN READER (WAI-ARIA tabs, the behaviour the Radix Tabs it
 * replaced had). Roving tabindex: the current tab is the one tab stop (`0`),
 * the others are `-1`. ArrowRight/ArrowLeft move to the next/previous tab and
 * wrap; Home/End go to the first/last. Selection follows focus (right for two
 * URL sections), so each move calls `onChange(key)` and focuses the new tab.
 * Every tab has an id and `aria-controls` the panel; `SectionPanel` below is
 * that panel, `role="tabpanel"` labelled by the current tab. Both take the
 * same `idPrefix` (default: the strip's testid).
 *
 * `onChange` fires for the CURRENT item too: a page that has a deeper view
 * under a section (a topic under Guides) uses the tap to come back up.
 *
 * The pill's shadow is the board's own value, `0 1px 2px rgba(15,23,42,0.12)`
 * (`TrainingHome.dc.html`, round 10): the kit has no hairline-shadow token.
 * The track is `--nw-line-faint` (#F1F5F9), the board's track colour.
 *
 * Reused by Plan (#1164) with its own items; nothing here is Training's.
 */
const TRACK_STYLE = {
  display: "flex",
  flexWrap: "nowrap",
  gap: 2,
  padding: 4,
  background: "var(--nw-line-faint)",
  borderRadius: "var(--nw-radius-control)",
  overflowX: "auto",
};

const ITEM_BASE = {
  flex: "1 0 auto",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "var(--nw-size-tap)",
  padding: "0 14px",
  border: 0,
  borderRadius: 8,
  whiteSpace: "nowrap",
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  fontSize: 15,
  cursor: "pointer",
};

const CURRENT = {
  backgroundColor: "var(--nw-surface)",
  color: "var(--nw-ink)",
  fontWeight: 600,
  boxShadow: "0 1px 2px rgba(15,23,42,0.12)",
};

const OTHER = {
  backgroundColor: "transparent",
  color: "var(--nw-ink-muted)",
  fontWeight: 500,
};

const panelId = (idPrefix) => `${idPrefix}-panel`;
const tabId = (idPrefix, key) => `${idPrefix}-tab-${key}`;

export function SectionStrip({ items, currentKey, onChange, idPrefix, className = "", "data-testid": testId = "sections", ...rest }) {
  const currentRef = useRef(null);
  const tabRefs = useRef({});
  const prefix = idPrefix || testId;

  useEffect(() => {
    const el = currentRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ inline: "nearest", block: "nearest" });
    }
  }, [currentKey]);

  const choose = (key) => {
    if (typeof onChange === "function") onChange(key);
  };

  const onKeyDown = (event, index) => {
    const last = items.length - 1;
    let next = null;
    if (event.key === "ArrowRight") next = index === last ? 0 : index + 1;
    else if (event.key === "ArrowLeft") next = index === 0 ? last : index - 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = last;
    if (next === null) return;
    event.preventDefault();
    const target = items[next];
    const el = tabRefs.current[target.key];
    if (el && typeof el.focus === "function") el.focus();
    if (target.key !== currentKey) choose(target.key);
  };

  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      data-testid={testId}
      className={`[-ms-overflow-style:none] [scrollbar-width:none] [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden ${className}`.trim()}
      style={TRACK_STYLE}
      {...rest}
    >
      {items.map((item, index) => {
        const current = item.key === currentKey;
        return (
          <button
            key={item.key}
            ref={(el) => {
              tabRefs.current[item.key] = el;
              if (current) currentRef.current = el;
            }}
            id={tabId(prefix, item.key)}
            type="button"
            role="tab"
            aria-selected={current ? "true" : "false"}
            aria-controls={panelId(prefix)}
            tabIndex={current ? 0 : -1}
            data-testid={item.testId}
            onClick={() => choose(item.key)}
            onKeyDown={(e) => onKeyDown(e, index)}
            style={{ ...ITEM_BASE, ...(current ? CURRENT : OTHER) }}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The tabpanel a `SectionStrip` controls: one wrapper around the current
 * section's body, labelled by the current tab. Same `idPrefix` as the strip.
 */
export function SectionPanel({ idPrefix, currentKey, children, ...rest }) {
  return (
    <div role="tabpanel" id={panelId(idPrefix)} aria-labelledby={tabId(idPrefix, currentKey)} {...rest}>
      {children}
    </div>
  );
}

export default SectionStrip;
